import { forwardRef, type HTMLAttributes, type ReactNode, useCallback } from 'react';
import { cn } from '../../../utils';
import { CodeBlock } from '../CodeBlock';

// Types for react-markdown - the consuming app provides the actual implementation
type MarkdownComponents = Record<string, React.ComponentType<Record<string, unknown>>>;

export interface MarkdownRendererProps extends HTMLAttributes<HTMLDivElement> {
  /** The markdown content to render */
  content: string;
  /** Optional keyword highlighting */
  keywords?: string[];
  /** Whether to render in compact mode (less spacing) */
  compact?: boolean;
  /** Callback when a link is clicked */
  onLinkClick?: (url: string) => void;
  /** Custom code block renderer (defaults to DS CodeBlock) */
  renderCodeBlock?: (code: string, language?: string) => ReactNode;
  /**
   * ReactMarkdown component from the consuming application.
   * The design system does not bundle react-markdown - it must be passed in.
   */
  ReactMarkdown: React.ComponentType<{
    children: string;
    remarkPlugins?: unknown[];
    components?: MarkdownComponents;
  }>;
  /**
   * remark-gfm plugin from the consuming application.
   * The design system does not bundle remark-gfm - it must be passed in.
   */
  remarkGfm?: unknown;
}

/**
 * MarkdownRenderer - Renders markdown content with consistent styling
 *
 * This component provides a standardized way to render markdown content
 * in chat interfaces with proper dark mode support and optional keyword
 * highlighting.
 *
 * Note: react-markdown and remark-gfm are NOT bundled with the design system.
 * The consuming application must pass these as props.
 *
 * @example
 * ```tsx
 * import ReactMarkdown from 'react-markdown';
 * import remarkGfm from 'remark-gfm';
 *
 * <MarkdownRenderer
 *   content="# Hello **World**"
 *   ReactMarkdown={ReactMarkdown}
 *   remarkGfm={remarkGfm}
 * />
 * ```
 */
export const MarkdownRenderer = forwardRef<HTMLDivElement, MarkdownRendererProps>(
  (
    {
      className,
      content,
      keywords = [],
      compact = false,
      onLinkClick,
      renderCodeBlock,
      ReactMarkdown,
      remarkGfm,
      ...props
    },
    ref
  ) => {
    // Highlight keywords in text content
    const highlightKeywords = useCallback(
      (text: string): ReactNode => {
        if (keywords.length === 0 || !text) {
          return text;
        }

        // Create a regex pattern that matches any of the keywords (case-insensitive)
        const pattern = new RegExp(
          `(${keywords.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
          'gi'
        );

        const parts = text.split(pattern);
        if (parts.length === 1) {
          return text;
        }

        return parts.map((part, index) => {
          const isKeyword = keywords.some(
            (k) => k.toLowerCase() === part.toLowerCase()
          );
          if (isKeyword) {
            return (
              <mark
                key={index}
                className="bg-amber-100 text-amber-900 px-0.5 rounded dark:bg-amber-500/20 dark:text-amber-200"
              >
                {part}
              </mark>
            );
          }
          return part;
        });
      },
      [keywords]
    );

    // Handle link clicks
    const handleLinkClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        if (onLinkClick) {
          e.preventDefault();
          onLinkClick(href);
        }
      },
      [onLinkClick]
    );

    // Default code block renderer using DS CodeBlock
    const defaultRenderCodeBlock = useCallback(
      (code: string, language?: string) => (
        <CodeBlock
          code={code}
          language={language}
          collapsible={false}
          defaultCollapsed={false}
          showLineNumbers={false}
        />
      ),
      []
    );

    const codeBlockRenderer = renderCodeBlock || defaultRenderCodeBlock;

    // Spacing classes based on compact mode
    const spacing = {
      paragraph: compact ? 'mb-2 last:mb-0' : 'mb-4 last:mb-0',
      list: compact ? 'mb-2 pl-5 space-y-1' : 'mb-4 pl-6 space-y-2',
      heading: compact ? 'mb-2' : 'mb-3',
      blockquote: compact ? 'my-2' : 'my-4',
      pre: compact ? 'mb-2' : 'mb-4',
      image: compact ? 'my-2' : 'my-4',
    };

    // Custom markdown components
    const components: MarkdownComponents = {
      p: ({ children }: { children?: ReactNode }) => (
        <p className={spacing.paragraph}>
          {typeof children === 'string' ? highlightKeywords(children) : children}
        </p>
      ),
      ul: ({ children }: { children?: ReactNode }) => (
        <ul className={cn(spacing.list, 'list-disc')}>{children}</ul>
      ),
      ol: ({ children }: { children?: ReactNode }) => (
        <ol className={cn(spacing.list, 'list-decimal')}>{children}</ol>
      ),
      li: ({ children }: { children?: ReactNode }) => (
        <li className="text-slate-800 dark:text-slate-200">
          {typeof children === 'string' ? highlightKeywords(children) : children}
        </li>
      ),
      code: ({
        children,
        className: codeClassName,
      }: {
        children?: ReactNode;
        className?: string;
      }) => {
        // Check if this is a code block (has language class) or inline code
        const isBlock = codeClassName?.startsWith('language-');
        const language = isBlock
          ? codeClassName?.replace('language-', '')
          : undefined;

        if (isBlock && typeof children === 'string') {
          return codeBlockRenderer(children.trim(), language);
        }

        // Inline code
        return (
          <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-sm font-mono text-slate-800 dark:text-slate-200">
            {children}
          </code>
        );
      },
      pre: ({ children }: { children?: ReactNode }) => {
        // The pre element wraps code blocks - we handle rendering in the code component
        // Just pass through the children which will be the code element
        return <div className={spacing.pre}>{children}</div>;
      },
      img: ({ src, alt }: { src?: string; alt?: string }) => (
        <img
          src={src || ''}
          alt={alt || 'Image'}
          className={cn(
            'max-w-full h-auto rounded-xl shadow-lg block',
            spacing.image
          )}
          style={{ maxHeight: '600px' }}
          crossOrigin="anonymous"
        />
      ),
      a: ({ href, children }: { href?: string; children?: ReactNode }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => href && handleLinkClick(e, href)}
          className="text-amber-600 hover:text-amber-700 underline dark:text-amber-400 dark:hover:text-amber-300"
        >
          {children}
        </a>
      ),
      blockquote: ({ children }: { children?: ReactNode }) => (
        <blockquote
          className={cn(
            'border-l-4 border-slate-300 pl-4 text-slate-600 italic',
            'dark:border-slate-600 dark:text-slate-400',
            spacing.blockquote
          )}
        >
          {children}
        </blockquote>
      ),
      h1: ({ children }: { children?: ReactNode }) => (
        <h1
          className={cn(
            'text-2xl font-bold text-slate-900 dark:text-slate-100',
            spacing.heading
          )}
        >
          {children}
        </h1>
      ),
      h2: ({ children }: { children?: ReactNode }) => (
        <h2
          className={cn(
            'text-xl font-bold text-slate-900 dark:text-slate-100',
            spacing.heading
          )}
        >
          {children}
        </h2>
      ),
      h3: ({ children }: { children?: ReactNode }) => (
        <h3
          className={cn(
            'text-lg font-semibold text-slate-900 dark:text-slate-100',
            spacing.heading
          )}
        >
          {children}
        </h3>
      ),
      h4: ({ children }: { children?: ReactNode }) => (
        <h4
          className={cn(
            'text-base font-semibold text-slate-900 dark:text-slate-100',
            spacing.heading
          )}
        >
          {children}
        </h4>
      ),
      h5: ({ children }: { children?: ReactNode }) => (
        <h5
          className={cn(
            'text-sm font-semibold text-slate-900 dark:text-slate-100',
            spacing.heading
          )}
        >
          {children}
        </h5>
      ),
      h6: ({ children }: { children?: ReactNode }) => (
        <h6
          className={cn(
            'text-sm font-medium text-slate-900 dark:text-slate-100',
            spacing.heading
          )}
        >
          {children}
        </h6>
      ),
      table: ({ children }: { children?: ReactNode }) => (
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            {children}
          </table>
        </div>
      ),
      thead: ({ children }: { children?: ReactNode }) => (
        <thead className="bg-slate-50 dark:bg-slate-800">{children}</thead>
      ),
      tbody: ({ children }: { children?: ReactNode }) => (
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {children}
        </tbody>
      ),
      tr: ({ children }: { children?: ReactNode }) => <tr>{children}</tr>,
      th: ({ children }: { children?: ReactNode }) => (
        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider dark:text-slate-300">
          {children}
        </th>
      ),
      td: ({ children }: { children?: ReactNode }) => (
        <td className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300">
          {children}
        </td>
      ),
      hr: () => <hr className="my-4 border-slate-200 dark:border-slate-700" />,
      strong: ({ children }: { children?: ReactNode }) => (
        <strong className="font-semibold">{children}</strong>
      ),
      em: ({ children }: { children?: ReactNode }) => <em>{children}</em>,
      del: ({ children }: { children?: ReactNode }) => (
        <del className="line-through text-slate-500 dark:text-slate-400">
          {children}
        </del>
      ),
    };

    const remarkPlugins = remarkGfm ? [remarkGfm] : [];

    return (
      <div
        ref={ref}
        className={cn(
          'text-slate-800 leading-relaxed dark:text-slate-200',
          className
        )}
        {...props}
      >
        <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
          {content}
        </ReactMarkdown>
      </div>
    );
  }
);
MarkdownRenderer.displayName = 'MarkdownRenderer';
